import { Component, Host, h, Prop } from '@stencil/core';
import type { JSX } from '@stencil/core/internal';
import type {
  CssClassMap,
  TableCell,
  TableContent,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-table',
  styleUrl: 'cms-table.scss',
  shadow: true,
})
export class CmsTable {
  /**
   * headline layout like full or two third center
   */
  @Prop({ reflect: true }) layout: 'full' | 'two-thirds' = 'full';
  /**
   * set the variant
   */
  @Prop() variant: 'default' | 'striped' = 'default';

  /**
   * set the headerVariant
   */
  @Prop() headerVariant: 'none' | 'top' | 'left' | 'top-left' = 'none';

  /**
   * set the content
   */
  @Prop({ mutable: true }) content: string | object;

  private data: TableContent;

  private hasHeaderRow = ['top', 'top-left'].includes(this.headerVariant);
  private hasHeaderLeft = ['left', 'top-left'].includes(this.headerVariant);

  private tableClassMap(): CssClassMap {
    return {
      [this.variant]: true,
      [this.headerVariant]: true,
      [this.layout]: true,
      'with-header': this.hasHeaderRow,
    };
  }

  componentWillLoad() {
    // data
    this.data =
      typeof this.content === 'string'
        ? JSON.parse(this.content)
        : this.content;
  }

  private renderRow(row: Array<TableCell>, cellTag = 'td'): JSX.Element {
    /* Clean-Up row first: Remove unnecessary rowspans */
    const firstRowSpan = row[0].rowspan;
    if (
      row.every((cell) => cell.rowspan > 1 && cell.rowspan === firstRowSpan)
    ) {
      for (const cell of row) {
        cell.rowspan = 1;
      }
    }

    return (
      <tr>
        {row.map((cell) => {
          let tag = cellTag;
          /* 
            Mark first column as th, if hasHeaderLeft - 
            we can't use the generic index here, because of the option to cells
          */
          if (cell.column === 0 && cellTag !== 'th') {
            tag = this.hasHeaderLeft ? 'th' : 'td';
          }

          return this.renderCell(cell, tag);
        })}
      </tr>
    );
  }

  private renderCell(cell: TableCell, tag = 'td'): JSX.Element {
    const TagType = tag;
    const cellStyle = {
      [`text-${cell.halign}`]: true,
      [`text-${cell.valign}`]: true,
    };

    return (
      <TagType
        class={cellStyle}
        rowSpan={cell.rowspan}
        colSpan={cell.colspan}
        innerHTML={cell.content}
        tabindex='0'
      />
    );
  }

  render() {
    return (
      <Host>
        <div class='table-container'>
          <div class='table-container-inner'>
            <table class={this.tableClassMap()}>
              {this.hasHeaderRow ? (
                <thead>{this.renderRow(this.data.rows[0], 'th')}</thead>
              ) : null}
              <tbody>
                {this.data.rows
                  .slice(this.hasHeaderRow ? 1 : 0)
                  .map((row) => this.renderRow(row))}
              </tbody>
            </table>
          </div>
        </div>
      </Host>
    );
  }
}
